import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator';
import { z } from 'zod';

import { countries } from '../../../../Auth';

export const US_COUNTRY_ID = 'US';

export const createShippingAddressSchema = (type: 'create' | 'update') => {
  const baseSchema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
    line1: z.string().trim().min(1, 'Address is required'),
    line2: z.string().trim().optional(),
    city: z.string().trim().min(1, 'City is required'),
    state: z.string().trim().optional(),
    country: z.string().min(1, 'Country is required'),
    postalCode: z.string().trim().min(1, 'Zip is required'),
    phone: z.string().trim().optional(),
    countryV2: z.string().trim().optional(),
  });

  // Add id field only for update type
  const schemaWithId =
    type === 'update'
      ? baseSchema.extend({
          id: z.string().trim().min(1, 'ID is required for updates'),
        })
      : baseSchema;

  return schemaWithId
    .refine(
      ({ country, postalCode }) => {
        // Find country by value (country code) instead of id
        const countryCode = countries.find((c) => c.value === country)?.value;

        return postalCode &&
          countryCode &&
          postcodeValidatorExistsForCountry(countryCode)
          ? postcodeValidator(postalCode, countryCode)
          : true;
      },
      {
        path: ['postalCode'],
        message: 'Postal code is invalid',
      }
    )
    .refine(
      ({ country, state }) => {
        // State is required only for USA (first country in the list)
        // Check by country code instead of id
        if (country === US_COUNTRY_ID) {
          return state && state.trim().length > 0;
        }
        return true;
      },
      {
        path: ['state'],
        message: 'State is required for US addresses',
      }
    );
};

export type TShippingAddressFormData = z.infer<
  ReturnType<typeof createShippingAddressSchema>
>;
